import type { Playbook } from "../lib/types";
import { Tag } from "./primitives";

/**
 * Who may read what a playbook's runs put into the shared memory.
 *
 * **Private is the one that carries colour.** Public is the default state of
 * this system — findings cross engagements — so it is the quiet stamp. Private
 * is the exception and is marked by a lock drawn in the signal: different by
 * shape, word *and* colour, the way status is a dot and a label. The word
 * stays ink, because orange as small text does not read (#124).
 *
 * It is a `Tag`, the one shape for a fact about a thing; it had a round pill
 * of its own beside four other shapes doing the same job.
 *
 * It is shown *before* a run starts rather than reported afterwards, for the
 * same reason the New Engagement form names the shared graph next to the
 * isolate switch: "my findings became visible to my colleagues" is not
 * something to discover later.
 */
export function AccessStamp({
  access,
  className = "",
}: {
  access: Playbook["access"];
  className?: string;
}) {
  const shared = access !== "private";
  return (
    <Tag
      strong={!shared}
      className={className}
      title={
        shared
          ? "Findings from this playbook are readable by every engagement — that is what cross-engagement memory is for."
          : "Findings from this playbook stay with the user who ran it. No other engagement sees them."
      }
    >
      {/* A mark as well as the word: the two differ by shape, not by hue. */}
      {shared ? (
        <span aria-hidden className="size-1.5 rounded-full bg-muted" />
      ) : (
        <svg
          aria-hidden
          width="9"
          height="10"
          viewBox="0 0 9 10"
          fill="none"
          stroke="var(--signal)"
          strokeWidth="1.2"
        >
          <rect x="1" y="4.5" width="7" height="5" rx="1" />
          <path d="M2.75 4.5V3a1.75 1.75 0 0 1 3.5 0v1.5" />
        </svg>
      )}
      {shared ? "shared" : "private"}
    </Tag>
  );
}
